import { Injectable, Injector, inject } from '@angular/core';
import { WorkflowModuleViewState } from '@los-workspace/workflow-runtime';
import { OpportunityShellPersistenceService } from './opportunity-shell-persistence.service';
import { OpportunityWorkflowBridge } from './opportunity-workflow.bridge';

export type OpportunityView =
  | 'list'
  | 'create'
  | 'detail'
  | 'customer-search'
  | 'credit-bureau';

export type OpportunityDetailTab =
  | 'lead-details'
  | 'loan-details'
  | 'address-details'
  | 'source-details'
  | 'activity';

export type OpportunityDetailAction =
  | 'assign_to_rm'
  | 'follow_up'
  | 'reject_opportunity'
  | 'convert_to_application';

/** Internal navigation params — replaces query params / route segments inside Opportunities. */
export interface OpportunityViewParams {
  opportunityId?: string;
  id?: string;
  applicantType?: string;
  applicantStatus?: string;
  partyId?: string;
  customerId?: string;
  customerName?: string;
  detailTab?: OpportunityDetailTab;
  action?: OpportunityDetailAction;
  wfStep?: string;
  tab?: string;
  mode?: string;
  returnTo?: string;
  idProofType?: string;
  idProofNumber?: string;
  /** Persisted bureau pull row id — load report without re-resolving from shell. */
  bureauReportId?: string;
  bureauScore?: string;
  /** The shell view that opened Credit Bureau — used for deterministic back-navigation. */
  returnView?: 'create' | 'detail';
  contact?: string;
  emailId?: string;
  genderCode?: string;
  dob?: string;
  dateOfIncorporation?: string;
  pan?: string;
  address?: string;
  addressLine1?: string;
  addressLine2?: string;
  pinCode?: string;
  city?: string;
  district?: string;
  stateCode?: string;
  countryCode?: string;
  addressTypeCode?: string;
  /** Existing-customer search replay (individual). */
  name?: string;
  aadhar?: string;
  /** Existing-customer search replay (corporate). */
  organizationName?: string;
  gstNumber?: string;
  incorporationRegistrationNumber?: string;
  searchSubmitted?: string;
  /** Set when returning from existing-customer results to reopen the search dialog. */
  reopenExistingCustomerSearch?: string;
}

@Injectable({ providedIn: 'root' })
export class OpportunityNavigationState extends WorkflowModuleViewState<
  OpportunityView,
  OpportunityViewParams
> {
  private readonly persistence = inject(OpportunityShellPersistenceService);
  private readonly injector = inject(Injector);
  private persistenceEnabled = false;

  constructor() {
    super();
    this.replaceView('list', {});
  }

  /** Call after shell bootstrap completes to begin persisting navigation. */
  enablePersistence(): void {
    this.persistenceEnabled = true;
    this.persistCurrent();
  }

  disablePersistence(): void {
    this.persistenceEnabled = false;
  }

  override setView(view: OpportunityView, params: Partial<OpportunityViewParams> = {}): void {
    const previous = this.view();
    super.setView(view, params);
    this.onViewChanged(previous);
  }

  override replaceView(view: OpportunityView, params: OpportunityViewParams): void {
    const previous = this.view();
    super.replaceView(view, params);
    this.onViewChanged(previous);
  }

  override patchParams(params: Partial<OpportunityViewParams>): void {
    const before = JSON.stringify(this.params());
    super.patchParams(params);
    const after = JSON.stringify(this.params());
    if (before !== after) {
      this.persistCurrent();
    }
  }

  private persistCurrent(): void {
    if (!this.persistenceEnabled) {
      return;
    }
    this.persistence.saveNavigationState(this.view(), this.params());
  }

  private onViewChanged(previous: OpportunityView): void {
    this.persistCurrent();
    if (!this.persistenceEnabled) {
      return;
    }
    const next = this.view();
    if (next === 'list' && previous !== 'list') {
      queueMicrotask(() => {
        void this.injector.get(OpportunityWorkflowBridge).resetKvForListEntry();
      });
    }
  }
}
